import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { Box } from '@/UI/Box/Box';
import { CircularProgress } from '@/UI/CircularProgress/CircularProgress';
import { TextSmRg } from '@/UI/AppTypography/AppTypography';
import { AppDialog, AppDialogTitle, AppDialogContent, AppDialogActions } from '@/UI/AppDialog/AppDialog';
import { I18N_KEYS } from '@/language/keys';
import { color } from '@/colors';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { ZIndex } from '@/consts/z-indexes.consts';
import { AppCheckbox } from '@/UI/AppCheckbox/AppCheckbox';
import type { SxProps, Theme } from '@/UI/theme.exports';

type ConfirmationDialogProps = {
  open: boolean;
  title: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  withLoading?: boolean;
  onCancel?: () => void;
  onClose: () => void;
  onConfirm: (checkboxChecked?: boolean) => void | Promise<void>;
  withCheckbox?: boolean;
  checkboxLabel?: string;
  checkboxChecked?: boolean;
  titleSx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
};

/**
 * @deprecated use AppConfirmationDialog
 */
function ConfirmationDialog({
  open,
  onClose,
  onConfirm,
  onCancel,
  title,
  message,
  confirmText,
  cancelText,
  withLoading = false,
  withCheckbox = false,
  checkboxLabel,
  checkboxChecked: initialCheckboxChecked,
  titleSx,
  contentSx,
}: ConfirmationDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [checkboxChecked, setCheckboxChecked] = useState(initialCheckboxChecked ?? false);
  const { t } = useTranslation();

  useEffect(() => {
    if (open) {
      setCheckboxChecked(initialCheckboxChecked ?? false);
    }
  }, [open, initialCheckboxChecked]);

  const handleConfirm = useCallback(() => {
    const handleConfirmAction = async () => {
      setIsLoading(true);
      await onConfirm(withCheckbox ? checkboxChecked : undefined);
      setIsLoading(false);
    };
    void handleConfirmAction();
  }, [onConfirm, withCheckbox, checkboxChecked]);

  const onCloseInner = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onClose();
    },
    [onClose],
  );

  const onCancelInner = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onCancel?.();
      onClose();
    },
    [onCancel, onClose],
  );

  return (
    <AppDialog
      onClose={onCloseInner}
      open={open}
      sx={{ zIndex: ZIndex.Overlays.CONFIRMATION_DIALOGS }}
      slotProps={{
        paper: {
          sx: { background: color.Black92, p: 1 },
        },
      }}
    >
      <AppDialogTitle variant="body-lg-sb" sx={titleSx}>
        {title}
      </AppDialogTitle>
      {message && (
        <AppDialogContent sx={{ whiteSpace: 'pre-line', ...contentSx }}>
          <TextSmRg>{message}</TextSmRg>
        </AppDialogContent>
      )}
      {withCheckbox && (
        <Box sx={{ px: 3, pb: 1 }}>
          <AppCheckbox
            checked={checkboxChecked}
            onChange={(_, checked) => setCheckboxChecked(checked)}
            label={checkboxLabel}
          />
        </Box>
      )}
      <AppDialogActions>
        <ButtonContained size="small" mode="text" onClick={onCancelInner}>
          {cancelText || t(I18N_KEYS.GENERAL.CANCEL)}
        </ButtonContained>
        <ButtonContained
          size="small"
          onClick={handleConfirm}
          endIcon={withLoading && isLoading ? <CircularProgress size={16} color="inherit" /> : null}
          disabled={isLoading}
        >
          {confirmText || t(I18N_KEYS.GENERAL.CONFIRM)}
        </ButtonContained>
      </AppDialogActions>
    </AppDialog>
  );
}

export default ConfirmationDialog;
